// 차량 동시 편집 프레즌스 — getSocket() 위에 얹는 얇은 헬퍼 (#343)
// 서버 계약(고정): 클라 → 서버 car:presence:enter/leave/ping(20s)/list(최대 500)
//                  서버 → 클라 car:presence {carId,users} / car:presence:snapshot {cars:{carId:users[]}}
// 소켓이 없으면(로그아웃 등) 전부 no-op — 서버 미배포/구 서버에서도 조용히 아무 표시 없음.
//
// 말소대행(cancel_requests)·정산 항목(cancel_settlement_items) 동시 작업 표시가 같은 모양의 계약
// (이벤트 접두사만 다름)이라 팩토리로 일반화 — car 인스턴스는 이 파일에서 그대로 export,
// 나머지는 utils/cancelPresence.js · utils/settlementPresence.js 에서 이 팩토리를 재사용해 만든다.
import { getSocket } from './socket';

function createPresenceClient(opts) {
  var eventPrefix = opts.eventPrefix;   // 예: 'car:presence'
  var idKey = opts.idKey;               // 예: 'carId'
  var listKey = opts.listKey;           // 예: 'carIds'
  var snapshotKey = opts.snapshotKey;   // 예: 'cars' — 스냅샷 payload 의 맵 키(서버 계약)
  var PING_INTERVAL_MS = 20000;
  var MAX_LIST = 500;
  // id -> { timer, refs }. refs = 이 탭에서 그 id 를 연 화면 수 — 같은 id 를 두 화면이 동시에 열 수
  // 있고(말소: 보류 모달 + 수정 폼, 정산: 드로어 + 검수 패널), refcount 가 없으면 먼저 닫히는 쪽의
  // leave 가 아직 열려 있는 쪽의 서버 엔트리와 ping 타이머까지 지운다.
  var pingTimers = {};
  // 구독 중인 채널 — 화면 하나가 채널 하나를 갖는다. 같은 소켓 싱글턴을 두 화면이 구독하는 일이
  // 실제로 있고(정산 장부 + 말소증함), 서버 :snapshot 에는 **어느 요청의 응답인지 표시가 없다**
  // (계약 고정). 그래서 스냅샷 스코프 판정을 화면마다 흉내내지 않고 여기서 한 번만 한다 —
  // 채널이 마지막으로 requestList() 한 id 로 걸러 그 채널에만 전달.
  var channels = [];   // [{ ids, handler, subscribe, requestList }]

  function emitEnter(s, id) {
    var p = {}; p[idKey] = id;
    s.emit(eventPrefix + ':enter', p); // car 인스턴스 예: car:presence:enter
  }
  function startPing(id) {
    return setInterval(function () {
      var s2 = getSocket();
      if (s2) { var p = {}; p[idKey] = id; s2.emit(eventPrefix + ':ping', p); } // car 인스턴스 예: car:presence:ping
    }, PING_INTERVAL_MS);
  }
  // 소켓이 없어 enter 를 못 보낸 슬롯(refs>0, timer 없음)을 소켓이 생긴 시점에 따라잡는다.
  function resumePending(s) {
    Object.keys(pingTimers).forEach(function (id) {
      var slot = pingTimers[id];
      if (slot.timer) return;
      emitEnter(s, id);
      slot.timer = startPing(id);
    });
  }

  function enter(id) {
    if (!id) return;
    if (pingTimers[id]) { pingTimers[id].refs++; bindSocket(); return; } // 이미 이 탭에서 열려 있다 — 참조만 늘린다
    // 소켓이 없어도 **슬롯(refs)은 만든다** — 안 만들면 무소켓 순간에 연 화면이 refcount 에서 빠져
    // 나중에 연 화면 하나만 refs=1 로 잡히고, 먼저 닫히는 쪽 leave 가 아직 열려 있는 쪽의 서버
    // 엔트리·타이머까지 지운다. emit 과 ping 타이머만 건너뛰고, 소켓이 생기면 resumePending 이 따라잡는다.
    pingTimers[id] = { refs: 1, timer: null };
    var s = getSocket();
    if (s) { emitEnter(s, id); pingTimers[id].timer = startPing(id); }
    // 토큰 교체(회사 전환)로 소켓이 바뀌었으면 여기서 새 소켓으로 옮겨 붙고, 밀린 enter 도 보낸다.
    bindSocket();
  }

  function leave(id) {
    if (!id) return;
    var slot = pingTimers[id];
    if (slot) {
      if (slot.refs > 1) { slot.refs--; return; } // 아직 다른 화면이 이 id 를 열고 있다
      clearInterval(slot.timer); delete pingTimers[id];
    }
    var s = getSocket();
    if (s) { var p = {}; p[idKey] = id; s.emit(eventPrefix + ':leave', p); } // car 인스턴스 예: car:presence:leave
    unbindSocket();
  }

  // 재연결 복구 — 소켓 끊김 후 재연결되면 서버가 이전 enter/list 상태를 모른다.
  // pingTimers 에 남아있는(=아직 leave 안 한) id 전부 재enter + 채널별 마지막 요청 id 재요청.
  function onReconnect() {
    var s = getSocket();
    if (!s) return;
    Object.keys(pingTimers).forEach(function (id) {
      emitEnter(s, id);
      if (!pingTimers[id].timer) pingTimers[id].timer = startPing(id); // 무소켓으로 열린 슬롯의 ping 시작
    });
    channels.forEach(function (ch) {
      if (!ch.ids.length) return;
      var p2 = {}; p2[listKey] = ch.ids.slice(0, MAX_LIST);
      s.emit(eventPrefix + ':list', p2); // car 인스턴스 예: car:presence:list
    });
  }

  // 소켓 리스너는 인스턴스당 1벌 — 구독자마다 등록하면 connect 핸들러가 중복돼 :list 가 겹쳐 나간다.
  // 소켓 **인스턴스가 바뀌면**(utils/socket.js 는 토큰이 달라지면 옛 소켓을 버리고 새로 만든다 —
  // 회사 전환 중 리로드 없이 진행되는 경로가 있다) 옛 소켓에 걸린 구독은 조용히 죽는다. 그래서
  // subscribe 뿐 아니라 enter/requestList 에서도 이 함수를 불러 현재 소켓과 맞는지 확인한다.
  // 붙는 조건은 "구독 채널이 있거나(목록 화면) **열린 항목이 있거나**(enter 만 하는 단건 편집 화면)" —
  // 채널만 보면 채널 없이 enter 만 하는 화면(예: 검수 패널 단독)이 재연결 re-enter 도, 소켓 교체
  // 재부착도 못 받는다.
  var bound = null; // { s, off }
  function bindSocket() {
    if (!channels.length && !Object.keys(pingTimers).length) return;
    var s = getSocket();
    if (!s) return;
    if (bound && bound.s === s) { resumePending(s); return; }
    if (bound) bound.off(); // 토큰 변경(회사 전환/재로그인)으로 소켓이 교체됐다 — 옛 소켓 바인딩 폐기
    var onPresence = function (payload) { dispatch({ type: 'presence', payload: payload || {} }); };
    var onSnapshot = function (payload) { dispatchSnapshot(payload); };
    s.on(eventPrefix, onPresence);
    s.on(eventPrefix + ':snapshot', onSnapshot);
    s.on('connect', onReconnect);
    bound = { s: s, off: function () {
      s.off(eventPrefix, onPresence);
      s.off(eventPrefix + ':snapshot', onSnapshot);
      s.off('connect', onReconnect);
    } };
    resumePending(s);
  }
  // 붙는 조건과 대칭 — 구독 채널도, 열린 항목도 없을 때만 뗀다.
  function unbindSocket() {
    if (channels.length || Object.keys(pingTimers).length || !bound) return;
    bound.off(); bound = null;
  }

  function dispatch(evt) {
    channels.slice().forEach(function (ch) { if (ch.handler) ch.handler(evt); });
  }
  // 스냅샷은 **그 채널이 요청한 id** 만 담아 전달한다.
  //  - 응답에 있는 내 id  → 그 값 그대로(빈 배열이면 화면이 지운다)
  //  - 응답에 없는 내 id  → 손대지 않는다. 다른 채널의 응답일 수 있어 "없으니 지운다"로 볼 수 없다.
  //  - 내 id 가 하나도 없는 응답 → 그 채널에는 아예 전달하지 않는다.
  function dispatchSnapshot(payload) {
    var map = (payload && payload[snapshotKey]) || {};
    channels.slice().forEach(function (ch) {
      if (!ch.handler) return;
      var mine = {}; var has = false;
      ch.ids.forEach(function (id) {
        if (!Object.prototype.hasOwnProperty.call(map, id)) return;
        mine[id] = map[id] || []; has = true;
      });
      if (has) ch.handler({ type: 'snapshot', payload: mine });
    });
  }

  // 화면 하나가 채널 하나를 만들어 구독 + 목록 요청에 같이 쓴다(둘을 묶어야 스냅샷 스코프가 성립).
  function createChannel() {
    var ch = { ids: [], handler: null };
    ch.subscribe = function (handler) {
      ch.handler = handler;
      channels.push(ch);
      bindSocket();
      return function () {
        var i = channels.indexOf(ch);
        if (i >= 0) channels.splice(i, 1);
        ch.handler = null; ch.ids = [];
        unbindSocket();
      };
    };
    ch.requestList = function (ids) {
      var arr = (ids || []).slice(0, MAX_LIST);
      var next = {}; arr.forEach(function (id) { next[id] = 1; });
      // 직전 요청에서 빠진 id = 이 화면이 더 이상 안 보는 행. 서버는 빈 레코드를 응답에 담지 않고
      // (빈 목록이면 :list 를 보내지도 않으므로) 서버 응답으로 지워질 기회가 없다 → 여기서 통지.
      var dropped = ch.ids.filter(function (id) { return !next[id]; });
      ch.ids = arr;
      if (dropped.length && ch.handler) {
        var gone = {}; dropped.forEach(function (id) { gone[id] = []; });
        ch.handler({ type: 'snapshot', payload: gone });
      }
      bindSocket();
      var s = getSocket();
      if (!s || arr.length === 0) return;
      var p = {}; p[listKey] = arr;
      s.emit(eventPrefix + ':list', p); // car 인스턴스 예: car:presence:list
    };
    return ch;
  }

  // 탭 종료 시 열려있던 모든 ping 타이머 정리 + leave emit (best-effort)
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', function () {
      Object.keys(pingTimers).forEach(function (id) {
        pingTimers[id].refs = 1; // 탭이 닫히므로 refcount 와 무관하게 한 번씩 정리
        leave(id);
      });
    });
  }

  return { enter: enter, leave: leave, createChannel: createChannel };
}

// ── 표시 헬퍼 (프레즌스 3종 공용) ────────────────────────────────
// 서버 users[] 는 **접속(소켓) 단위**다 — 같은 계정이라도 접속이 둘이면 2항목({userId,name,since,sid,platform}).
// 같은 계정을 PC 웹과 모바일 앱에서 다른 사람이 쓰는 것이 실제 운영 조건이라, 본인 제외 기준은
// userId 가 아니라 **내 socket.id** 다(userId 로 걸러내면 그 둘이 서로를 못 본다).

// 내 socket.id — 재연결 때마다 바뀌므로 캐시하지 말고 매 판정 시점에 읽는다.
function mySid() {
  var s = getSocket();
  return (s && s.id) || '';
}

// 본인 접속 1개만 제외.
// sid 를 못 쓰는 두 경우 — ① 서버가 아직 sid 를 안 싣는 구 배포본, ② 재연결 창(socket.io-client 가
// disconnect 시 socket.id 를 지운다) — 에는 userId 로 폴백한다. 폴백이 없으면 **자기 자신이 자기
// 화면에 "작업 중"** 으로 뜬다(정확도는 떨어져도 본인이 본인을 보는 것보다 낫다).
function presenceOthers(users, sid, myUserId) {
  var arr = users || [];
  return arr.filter(function (u) {
    if (!u) return false;
    if (sid && u.sid) return u.sid !== sid;
    if (myUserId == null) return true; // 내가 누구인지도 모르면 걸러낼 기준이 없다
    return String(u.userId) !== String(myUserId);
  });
}

// 표시 이름. 같은 계정의 다른 접속이면 어느 기기인지 밝힌다 ("홍길동 (모바일)").
function presenceLabel(u, myUserId) {
  if (!u) return '';
  var name = u.name || '';
  if (myUserId != null && String(u.userId) === String(myUserId)) {
    return name + (u.platform === 'app' ? ' (모바일)' : ' (PC)');
  }
  return name;
}

// 화면이 쓰는 형태 — 본인 접속 제외 + 표시 이름(displayName) 부여. 점 title·배너 모두 이걸 읽는다.
// presenceOthers/presenceLabel 은 이 함수의 부품이지만 각각의 규칙을 단위 테스트로 고정한다
// (src/utils/__tests__/carPresence.test.js) — 그래서 export 를 유지한다.
function presenceOthersLabeled(users, sid, myUserId) {
  return presenceOthers(users, sid, myUserId).map(function (u) {
    return Object.assign({}, u, { displayName: presenceLabel(u, myUserId) });
  });
}

var carPresence = createPresenceClient({ eventPrefix: 'car:presence', idKey: 'carId', listKey: 'carIds', snapshotKey: 'cars' });

var enter = carPresence.enter;
var leave = carPresence.leave;
var createChannel = carPresence.createChannel;

export { createPresenceClient, enter, leave, createChannel, mySid, presenceOthers, presenceLabel, presenceOthersLabeled };
